import { splitWithoutEmptyStrings } from "./helpers";
import { storageSetItem, storageGetItem, storageRemoveItem } from "./storageHelper";

export class FormDataSaver {
    formId: string | null;
    form: HTMLFormElement;
    periodicFormDataSavingTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

    constructor(form: HTMLFormElement) {
        this.formId = form.getAttribute("id");
        this.form = form;
    }

    stopPeriodicFormDataSaving() {
        if (this.periodicFormDataSavingTimeout) {
            clearInterval(this.periodicFormDataSavingTimeout);
            this.periodicFormDataSavingTimeout = undefined;
        }
    }

    clearSavedFormData() {
        if (this.formId) {
            storageRemoveItem(this.formId);
        }
    }

    getFormDataAsJson(): string { // taken from https://stackoverflow.com/a/46774073/379279
        const formData = new FormData(this.form)
        const object = {};
        formData.forEach((value, key) => {
            // Reflect.has in favor of: object.hasOwnProperty(key)
            if (!Reflect.has(object, key)) {
                object[key] = value;
                return;
            }
            if (!Array.isArray(object[key])) {
                if (object[key] === "true") return; // duplicate checkbox value, we don't want to store it multiple times; more info: https://stackoverflow.com/questions/2697299/asp-net-mvc-why-is-html-checkbox-generating-an-additional-hidden-input
                object[key] = [object[key]];
            }
            object[key].push(value);
        });
        return JSON.stringify(object);
    }

    hasSavedFormData(): boolean {
        return this.formId !== null && storageGetItem(this.formId) !== null;
    }

    setupPeriodicFormDataSaving() {
        this.periodicFormDataSavingTimeout = setInterval(() => {
            if (!this.formId) return;

            const formDataAsJson = this.getFormDataAsJson();
            storageSetItem(this.formId, formDataAsJson);
        }, 1000);
    }

    loadSavedFormData() {
        if (!this.formId) return;

        const formDataAsJson = storageGetItem(this.formId);
        if (!formDataAsJson) throw new Error(`Cannot load saved form data for formId ${this.formId}`);
        const formDataObject = JSON.parse(formDataAsJson);

        Object.keys(formDataObject).forEach(elementName => {
            if (elementName === "files" || elementName === "__RequestVerificationToken") return;

            const elementValue = formDataObject[elementName];
            const element = $(`[name="${elementName}"`, `#${this.formId}`);
            if (element.length === 0) return;

            if (element.is("input[type='file']")) return;

            if (element.is("input[type='checkbox']")) {
                this._loadSavedFormDataForCheckbox(element, elementValue);
            } else if (element.is("input[type='radio']")) {
                this._loadSavedFormDataForRadioButtons(element, elementValue);
            } else {
                element.val(elementValue);

                if (element.next(".note-editor").length === 1) {
                    element.summernote("code", elementValue);
                }
                const elementSelectize = element[0].selectize;
                if (elementSelectize) {
                    if (elementSelectize.options[elementValue]) { // dropdown
                        elementSelectize.setValue(elementValue, false);
                    } else { // badges in an input
                        elementSelectize.clearOptions();
                        elementSelectize.clear();
                        splitWithoutEmptyStrings(elementValue, ",").forEach(item => {
                            elementSelectize.addOption({ value: item, text: item });
                            elementSelectize.addItem(item);
                        });
                    }
                }
            }
        });
    }

    private _loadSavedFormDataForCheckbox(element: JQuery<HTMLElement>, elementValue) {
        if (elementValue === "true" || elementValue === "false") {           
            element = $(element[0]); // normal checkbox, could be twice in the page due to: https://stackoverflow.com/questions/2697299/asp-net-mvc-why-is-html-checkbox-generating-an-additional-hidden-input
            element.prop("checked", elementValue === "true").trigger("change");
        }
        else {
            this._loadSavedFormDataForToggleButtonCheckboxes(element, elementValue);
        }
    }

    private _loadSavedFormDataForToggleButtonCheckboxes(element: JQuery<HTMLElement>, elementValue) {
        this._makeToggleButtonsLookUnselected(element);

        if (Array.isArray(elementValue)) {
            elementValue.forEach(value => {
                const checkboxWithGivenValue = element.filter(`[value='${value}']`);
                checkboxWithGivenValue.prop("checked", true);
                checkboxWithGivenValue.parent(".btn").addClass("active");
            });
        }
        else {
            const checkboxWithGivenValue = element.filter(`[value='${elementValue}']`);
            checkboxWithGivenValue.prop("checked", true);
            checkboxWithGivenValue.parent(".btn").addClass("active");
        }
    }

    private _makeToggleButtonsLookUnselected(element: JQuery<HTMLElement>) {
        element.parent(".btn").removeClass("active");
        element.removeAttr("checked");
    }

    private _loadSavedFormDataForRadioButtons(element: JQuery<HTMLElement>, elementValue) {
        this._makeToggleButtonsLookUnselected(element);
        const radioButtonInputWithGivenValue = element.filter(`[value='${elementValue}']`);
        radioButtonInputWithGivenValue.prop("checked", true);
        radioButtonInputWithGivenValue.parent(".btn").addClass("active");
    }
}

